import React, { useCallback, useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowLeft, Loader2, Phone, Mail, MapPin, Building2, Hash, User, Package,
  Clock, AlertTriangle, Save, Send, MessageSquarePlus, History, Receipt, Plus, Trash2, FileText,
  CheckCircle2, XCircle, ExternalLink,
} from "lucide-react";

const money = (n) => "$" + (Number(n) || 0).toLocaleString("en-AU", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
import { api, formatApiError } from "@/lib/api";
import { stageClasses, priorityClasses, fmtDate, isOverdue } from "@/lib/pipeline";
import { SalesReadiness } from '@/components/SalesReadiness';
import { FreightEstimator } from '@/components/FreightEstimator';
import Seo from "@/components/Seo";

const selectCls = "w-full min-h-[44px] rounded-sm border border-ei-mist bg-white px-3 py-2 text-sm text-ei-graphite focus:border-ei-navy outline-none";
export const AU_STATES = ["ACT", "NSW", "NT", "QLD", "SA", "TAS", "VIC", "WA"];

/** Australian postcode to state, matching the backend carve-outs for ACT and NT. */
export function stateForPostcode(postcode) {
  if (!/^\d{4}$/.test(postcode)) return "";
  const n = Number(postcode);
  if ((n >= 200 && n <= 299) || (n >= 2600 && n <= 2618) || (n >= 2900 && n <= 2920)) return "ACT";
  if (n >= 800 && n <= 899) return "NT";
  return { 1: "NSW", 2: "NSW", 3: "VIC", 4: "QLD", 5: "SA", 6: "WA", 7: "TAS", 8: "VIC", 9: "QLD" }[postcode[0]] || "";
}

function Row({ icon: Icon, label, children }) {
  return (
    <div className="flex items-start gap-3 py-2.5">
      <Icon className="w-4 h-4 text-ei-golddark mt-0.5 flex-shrink-0" />
      <div className="min-w-0">
        <div className="text-xs text-ei-graphite/55">{label}</div>
        <div className="text-sm text-ei-navy font-medium break-words">{children}</div>
      </div>
    </div>
  );
}

export default function EnquiryDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [enq, setEnq] = useState(null);
  const [meta, setMeta] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [note, setNote] = useState("");
  const [resending, setResending] = useState(false);
  const [quote, setQuote] = useState({ line_items: [], freight: 0, freight_source: null, gst_rate: 10, validity_days: 30, notes: "" });
  const [catalogue, setCatalogue] = useState([]);
  const [address, setAddress] = useState({ street: "", suburb: "", state: "", postcode: "" , residential: false });
  const [addressSaving, setAddressSaving] = useState(false);
  const [deliveryOverride, setDeliveryOverride] = useState(false);
  const [quoteSaving, setQuoteSaving] = useState(false);
  const [quoteSending, setQuoteSending] = useState(false);
  const [quoteDeciding, setQuoteDeciding] = useState(false);
  const [xeroBusy, setXeroBusy] = useState(false);
  const [pdfBusy, setPdfBusy] = useState(false);
  const [emailBusy, setEmailBusy] = useState(false);
  const [form, setForm] = useState({ stage: "", priority: "", owner_id: "", next_action: "", follow_up_due_at: "", lost_reason: "" });

  const load = useCallback(async () => {
    try {
      const [e, m, c] = await Promise.all([api.get(`/enquiries/${id}`), api.get("/meta"), api.get("/sales/catalogue")]);
      setEnq(e.data);
      setMeta(m.data);
      setCatalogue(c.data || []);
      const priceFor = (name) => Number((c.data || []).find((p) => p.name === name)?.unit_price || 0);
      const idFor = (name) => (c.data || []).find((p) => p.name === name)?.product_id || null;
      setAddress(e.data.delivery_address || { street: "", suburb: "", state: "", postcode: e.data.postcode || "", residential: false });
      if (e.data.quote) {
        setQuote({
          line_items: (e.data.quote.line_items || []).map((line) => ({ ...line, uiId: crypto.randomUUID(), unit_price: line.list_unit_price ?? line.unit_price })),
          freight: e.data.quote.freight || 0,
          freight_source: e.data.quote.freight_source || null,
          gst_rate: e.data.quote.gst_rate ?? 10,
          validity_days: e.data.quote.validity_days ?? 30,
          notes: e.data.quote.notes || "",
        });
        // A collection enquiry carrying freight was overridden to delivery — keep that on reload.
        setDeliveryOverride(Number(e.data.quote.freight || 0) > 0);
      } else {
        setDeliveryOverride(false);
        setQuote({
          line_items: (e.data.products || []).map((p) => ({ uiId: crypto.randomUUID(), product_id: p.product_id || idFor(p.name), name: p.name, quantity: p.quantity, unit_price: priceFor(p.name), price_source: priceFor(p.name) ? "catalogue" : null })),
          freight: 0, gst_rate: 10, validity_days: 30, notes: "", freight_source: null,
        });
      }
      setForm({
        stage: e.data.stage || "",
        priority: e.data.priority || "",
        owner_id: e.data.owner_id || "",
        next_action: e.data.next_action || "",
        follow_up_due_at: toLocalInput(e.data.follow_up_due_at),
        lost_reason: e.data.lost_reason || "",
      });
    } catch (err) {
      toast.error("Could not load enquiry");
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const result = new URLSearchParams(window.location.search).get("xero");
    if (!result) return;
    const msgs = { connected: ["success", "Xero connected — you can now create the draft invoice"], error: ["error", "Xero authorisation failed"], invalid: ["error", "Xero session expired — click Connect Xero again"], notenant: ["error", "No Xero organisation was selected"] };
    const [kind, text] = msgs[result] || ["error", "Xero connection did not complete"];
    toast[kind](text);
    window.history.replaceState({}, "", window.location.pathname);
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        stage: form.stage,
        priority: form.priority,
        owner_id: form.owner_id || "unassigned",
        next_action: form.next_action,
        follow_up_due_at: form.follow_up_due_at ? new Date(form.follow_up_due_at).toISOString() : "",
        lost_reason: form.lost_reason,
      };
      const { data } = await api.patch(`/enquiries/${id}`, payload);
      setEnq(data);
      setForm((f) => ({ ...f, follow_up_due_at: toLocalInput(data.follow_up_due_at) }));
      toast.success("Enquiry updated");
    } catch (err) {
      toast.error(formatApiError(err, "Could not save changes"));
    } finally {
      setSaving(false);
    }
  };

  const addNote = async () => {
    if (!note.trim()) return;
    try {
      const { data } = await api.post(`/enquiries/${id}/notes`, { body: note.trim() });
      setEnq(data);
      setNote("");
      toast.success("Note added");
    } catch (err) {
      toast.error(formatApiError(err, "Could not add note"));
    }
  };

  const resend = async () => {    setResending(true);
    try {
      const { data } = await api.post(`/enquiries/${id}/resend-notification`);
      setEnq(data.enquiry);
      toast.success(data.notification_status === "sent" ? "Notifications sent" : `Notification status: ${data.notification_status}`);
    } catch (err) {
      toast.error(formatApiError(err, "Could not resend notification"));
    } finally {
      setResending(false);
    }
  };

  const setQLine = (idx, key, val) => setQuote((q) => ({ ...q, line_items: q.line_items.map((l, i) => {
    if (i !== idx) return l;
    if (key !== 'name') return { ...l, [key]: val, ...(key === 'unit_price' ? { price_source: 'manual' } : {}) };
    const match = catalogue.find((p) => p.name === val);
    return { ...l, name: val, product_id: match?.product_id || null,
      ...(match ? { unit_price: Number(match.unit_price || 0), price_source: 'catalogue' } : {}) };
  }) }));
  const addQLine = () => setQuote((q) => {
    const first = catalogue[0];
    return { ...q, line_items: [...q.line_items, { uiId: crypto.randomUUID(), product_id: first?.product_id || null, name: first?.name || (meta?.products || [])[0] || "", quantity: 1, unit_price: Number(first?.unit_price || 0), price_source: first ? 'catalogue' : null }] };
  });
  const removeQLine = (idx) => setQuote((q) => ({ ...q, line_items: q.line_items.filter((_, i) => i !== idx) }));
  const isCollection = (enq?.fulfilment_preference || "").trim().toLowerCase() === "collection";
  const freightApplies = !isCollection || deliveryOverride;
  const qTotals = () => {
    const sub = quote.line_items.reduce((s, l) => s + (Number(l.quantity) || 0) * (Number(l.unit_price) || 0) * (1 - (Number(l.discount_percent) || 0) / 100), 0);
    const freight = freightApplies ? Number(quote.freight) || 0 : 0;
    const taxable = sub + freight;
    const gst = taxable * ((Number(quote.gst_rate) || 0) / 100);
    return { sub, freight, gst, total: taxable + gst };
  };
  const saveQuote = async () => {
    if (quote.line_items.length === 0) { toast.error("Add at least one line item"); return; }
    setQuoteSaving(true);
    try {
      const payload = {
        line_items: quote.line_items.map((l) => ({ product_id: l.product_id || null, name: l.name, quantity: Number(l.quantity), unit_price: Number(l.unit_price), discount_percent: Number(l.discount_percent || 0), discount_reason: l.discount_reason || null })),
        freight: freightApplies ? Number(quote.freight) || 0 : 0,
        freight_source: freightApplies ? quote.freight_source || null : "Collection from warehouse — no freight",
        gst_rate: Number(quote.gst_rate) || 0,
        validity_days: Number(quote.validity_days) || 30, notes: quote.notes || null,
      };
      const { data } = await api.put(`/enquiries/${id}/quote`, payload);
      setEnq(data);
      setQuote({ line_items: data.quote.line_items.map((line) => ({ ...line, uiId: crypto.randomUUID(), unit_price: line.list_unit_price ?? line.unit_price })), freight: data.quote.freight, freight_source: data.quote.freight_source || null, gst_rate: data.quote.gst_rate, validity_days: data.quote.validity_days, notes: data.quote.notes || '' });
      toast.success(`Quote v${data.quote.version} saved`);
    } catch (err) { toast.error(formatApiError(err, "Could not save quote")); }
    finally { setQuoteSaving(false); }
  };
  const sendQuote = async () => {
    setQuoteSending(true);
    try {
      const { data } = await api.post(`/enquiries/${id}/quote/send`);
      setEnq(data);
      toast.success(`Quote v${data.quote.version} released for customer acceptance`);
    } catch (err) { toast.error(formatApiError(err, "Could not send quote")); }
    finally { setQuoteSending(false); }
  };
  const declineQuote = async () => {
    const reason = window.prompt("Reason for declining (optional):", "") ?? "";
    setQuoteDeciding(true);
    try {
      const { data } = await api.post(`/enquiries/${id}/quote/decline`, { reason: reason || null });
      setEnq(data);
      toast.success(`Quote v${data.quote.version} declined`);
    } catch (err) { toast.error(formatApiError(err, "Could not decline quote")); }
    finally { setQuoteDeciding(false); }
  };

  const saveAddress = async () => {
    setAddressSaving(true);
    try {
      const { data } = await api.patch(`/enquiries/${id}`, { delivery_address: { ...address, suburb: address.suburb.toUpperCase() } });
      setEnq(data);
      setAddress(data.delivery_address);
      toast.success("Delivery address saved");
    } catch (err) { toast.error(formatApiError(err, "Could not save the delivery address")); }
    finally { setAddressSaving(false); }
  };

  const createXeroInvoice = async () => {
    setXeroBusy(true);
    try {
      const { data } = await api.post(`/enquiries/${id}/xero/invoice`);
      if (data.enquiry) setEnq(data.enquiry);
      toast.success(data.replayed ? "Xero draft invoice already exists — linked" : "Xero draft invoice created");
    } catch (err) { toast.error(formatApiError(err, "Could not create Xero invoice")); }
    finally { setXeroBusy(false); }
  };


  const connectXero = async () => {
    try {
      const { data } = await api.get(`/xero/connect`, { params: { return_to: `/staff/enquiries/${id}` } });
      if (data.authorize_url) window.location.assign(data.authorize_url);
    } catch (err) { toast.error(formatApiError(err, "Could not start Xero connection")); }
  };

  const downloadQuotePdf = async () => {
    setPdfBusy(true);
    try {
      const res = await api.get(`/enquiries/${id}/quote.pdf`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `EI-Quote-${enq.reference}-v${enq.quote?.version || 1}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(formatApiError(err, "Could not generate the PDF"));
    } finally { setPdfBusy(false); }
  };
  const emailQuote = async () => {
    setEmailBusy(true);
    try {
      const { data } = await api.post(`/enquiries/${id}/quote/email`);
      toast.success(`Quote emailed to ${data.to}`);
      load();
    } catch (err) {
      toast.error(formatApiError(err, "Could not email the quote"));
    } finally { setEmailBusy(false); }
  };

  if (loading) return <div className="flex items-center justify-center py-32"><Loader2 className="w-8 h-8 animate-spin text-ei-navy" /></div>;
  if (!enq) return (
    <div className="text-center py-24">
      <p className="text-ei-navy font-semibold">Enquiry not found.</p>
      <Link to="/staff/enquiries" className="mt-4 inline-block text-sm font-semibold text-ei-navy underline">Back to enquiries</Link>
    </div>
  );

  const isClosed = ["Lost", "Cancelled"].includes(form.stage);
  const activity = [...(enq.activity || [])].reverse();

  return (
    <div>
      <Seo title={`${enq.business_name} — EI Battery Workspace`} />
      <Link to="/staff/enquiries" className="inline-flex items-center gap-2 text-sm font-semibold text-ei-graphite/70 hover:text-ei-navy" data-testid="back-to-enquiries">
        <ArrowLeft className="w-4 h-4" /> All enquiries
      </Link>

      <div className="mt-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <div className="flex items-center gap-3 flex-wrap">
            <h1 className="text-2xl sm:text-3xl font-bold text-ei-navy">{enq.business_name}</h1>
            <span className={`text-xs font-semibold px-2.5 py-1 rounded-sm border ${stageClasses(enq.stage)}`} data-testid="detail-stage-badge">{enq.stage}</span>
          </div>
          <div className="mt-1 text-sm text-ei-graphite/60 tnum">{enq.reference} · received {fmtDate(enq.created_at, true)}</div>
        </div>
        {isOverdue(enq.follow_up_due_at, enq.stage) && (
          <div className="inline-flex items-center gap-2 text-sm font-semibold text-red-700 bg-red-50 border border-red-200 rounded-sm px-3 py-2" data-testid="overdue-badge">
            <AlertTriangle className="w-4 h-4" /> Follow-up overdue
          </div>
        )}
      </div>

      <div className="mt-6 grid lg:grid-cols-3 gap-6">
        {/* Left: customer + original enquiry */}
        <div className="lg:col-span-1 space-y-6">
          <div className="bg-white border border-ei-mist rounded-md p-5">
            <h2 className="font-display font-semibold text-ei-navy">Contact details</h2>
            <div className="mt-2 divide-y divide-ei-mist/70">
              <Row icon={User} label="Contact">{enq.contact_name}</Row>
              <Row icon={Phone} label="Mobile"><a href={`tel:${enq.mobile}`} className="hover:text-ei-golddark" data-testid="detail-call">{enq.mobile}</a></Row>
              <Row icon={Mail} label="Email"><a href={`mailto:${enq.email}`} className="hover:text-ei-golddark break-all" data-testid="detail-email">{enq.email}</a></Row>
              <Row icon={Building2} label="Trading name">{enq.trading_name || "—"}</Row>
              <Row icon={Hash} label="ABN"><span className="tnum">{enq.abn}</span></Row>
              <Row icon={MapPin} label="Postcode / fulfilment"><span className="tnum">{enq.postcode}</span> · {enq.fulfilment_preference}</Row>
            </div>
          </div>

          <div className="bg-white border border-ei-mist rounded-md p-5">
            <h2 className="font-display font-semibold text-ei-navy">Requested products</h2>
            <ul className="mt-3 space-y-2">
              {enq.products.map((p, i) => (
                <li key={`${p.name}-${i}`} className="flex items-center justify-between text-sm">
                  <span className="flex items-center gap-2 text-ei-navy"><Package className="w-4 h-4 text-ei-golddark" /> {p.name}</span>
                  <span className="font-display font-bold tnum text-ei-navy">×{p.quantity}</span>
                </li>
              ))}
            </ul>
            {enq.timeframe && <div className="mt-3 text-sm text-ei-graphite/80">Timeframe: {enq.timeframe}</div>}
            {enq.notes && <div className="mt-3 text-sm text-ei-graphite/80 bg-ei-white rounded-sm p-3 border border-ei-mist">{enq.notes}</div>}
            <div className="mt-3 text-xs text-ei-graphite/50">
              Contractor declared: {enq.contractor_declaration ? "Yes" : "No"} · Consent: {enq.consent ? "Yes" : "No"} · Marketing: {enq.marketing_consent ? "Opted in" : "No"}
            </div>
            <div className="mt-2 text-xs text-ei-graphite/50 flex items-center justify-between gap-2" data-testid="notification-status">
              <span>
                Email notifications:{" "}
                <span className={`font-semibold ${enq.notification_status === "sent" ? "text-green-700" : enq.notification_status === "failed" ? "text-red-700" : "text-ei-golddark"}`}>
                  {enq.notification_status === "sent" ? "sent" : enq.notification_status === "partial" ? "partly sent" : enq.notification_status === "failed" ? "failed" : enq.notification_status === "throttled" ? "temporarily throttled" : "queued"}
                </span>
              </span>
              <button onClick={resend} disabled={resending} className="inline-flex items-center gap-1 font-semibold text-ei-navy hover:text-ei-golddark disabled:opacity-50" data-testid="resend-notification">
                {resending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />} Resend
              </button>
            </div>
          </div>
        </div>

        {/* Middle: management */}
        <div className="lg:col-span-1 space-y-6">
          <div className="bg-white border border-ei-mist rounded-md p-5">
            <h2 className="font-display font-semibold text-ei-navy">Manage</h2>
            <div className="mt-4 space-y-4">
              <div>
                <label className="block text-xs font-semibold text-ei-navy mb-1">Stage</label>
                <select value={form.stage} onChange={(e) => setForm((f) => ({ ...f, stage: e.target.value }))} className={selectCls} data-testid="select-stage">
                  {meta?.stages.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
                {form.stage === "Quote sent" && <p className="mt-1 text-xs text-ei-golddark">Saving this sets a 2-hour follow-up (Australia/Melbourne time).</p>}
              </div>
              <div>
                <label className="block text-xs font-semibold text-ei-navy mb-1">Priority</label>
                <select value={form.priority} onChange={(e) => setForm((f) => ({ ...f, priority: e.target.value }))} className={selectCls} data-testid="select-priority">
                  {meta?.priorities.map((p) => <option key={p} value={p}>{p}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs font-semibold text-ei-navy mb-1">Owner</label>
                <select value={form.owner_id} onChange={(e) => setForm((f) => ({ ...f, owner_id: e.target.value }))} className={selectCls} data-testid="select-owner">
                  <option value="">Unassigned</option>
                  {meta?.staff.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs font-semibold text-ei-navy mb-1">Next action</label>
                <input value={form.next_action} onChange={(e) => setForm((f) => ({ ...f, next_action: e.target.value }))} className={selectCls} placeholder="e.g. Call to confirm quantities" data-testid="input-next-action" />
              </div>
              <div>
                <label className="block text-xs font-semibold text-ei-navy mb-1">Follow-up due</label>
                <input type="datetime-local" value={form.follow_up_due_at} onChange={(e) => setForm((f) => ({ ...f, follow_up_due_at: e.target.value }))} className={selectCls} data-testid="input-followup" />
              </div>
              {isClosed && (
                <div>
                  <label className="block text-xs font-semibold text-ei-navy mb-1">Lost / cancelled reason</label>
                  <textarea rows={2} value={form.lost_reason} onChange={(e) => setForm((f) => ({ ...f, lost_reason: e.target.value }))} className={selectCls} data-testid="input-lost-reason" />
                </div>
              )}
              <button onClick={save} disabled={saving} className="w-full font-display text-sm font-bold bg-ei-navy text-ei-white px-5 py-3 rounded-sm hover:bg-ei-ink transition-colors min-h-[44px] inline-flex items-center justify-center gap-2 disabled:opacity-60" data-testid="save-enquiry">
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save changes
              </button>
            </div>
          </div>

          {isCollection && !deliveryOverride ? (
            <div className="bg-white border border-ei-mist rounded-md p-5" data-testid="collection-card">
              <h2 className="font-display font-semibold text-ei-navy flex items-center gap-2"><MapPin className="w-4 h-4 text-ei-golddark" /> Collection from warehouse</h2>
              <p className="mt-2 text-sm text-ei-graphite/80">The customer chose <strong>Collection</strong> on their enquiry, so no delivery address or freight is needed. Freight stays at $0.00 on the quote.</p>
              {enq.quote?.pickup ? (
                <div className="mt-3 rounded-sm border border-ei-mist bg-ei-white/60 p-3 text-sm" data-testid="pickup-details">
                  <p className="font-semibold text-ei-navy">{enq.quote.pickup.warehouse} warehouse</p>
                  <p className="mt-1 text-ei-graphite/80">{enq.quote.pickup.address}</p>
                  <p className="mt-1 text-ei-graphite/80">{enq.quote.pickup.hours}</p>
                  <p className="mt-2 text-xs text-ei-graphite/60">Printed on the quote PDF and included in the quote email.{enq.quote.pickup.stock_confirmed === false ? " Stock is not confirmed at this warehouse — check before promising a pickup date." : ""}</p>
                </div>
              ) : (
                <p className="mt-3 text-xs text-ei-graphite/60">Save the quote to confirm which warehouse the customer collects from — the address and pickup hours are then added to the quote PDF and email.</p>
              )}
              <p className="mt-2 text-xs text-ei-graphite/60">Enquiry postcode <span className="tnum">{enq.postcode}</span> · collection details are confirmed with the customer before pickup.</p>
              <button onClick={() => setDeliveryOverride(true)} className="mt-4 w-full font-display text-sm font-bold text-ei-navy border border-ei-navy/25 px-5 py-3 rounded-sm hover:border-ei-navy min-h-[44px]" data-testid="switch-to-delivery">
                Customer has switched to delivery
              </button>
            </div>
          ) : (
          <div className="bg-white border border-ei-mist rounded-md p-5" data-testid="delivery-address-card">
            <h2 className="font-display font-semibold text-ei-navy flex items-center gap-2"><MapPin className="w-4 h-4 text-ei-golddark" /> Delivery address</h2>
            <p className="mt-1 text-xs text-ei-graphite/60">Used by the freight estimator and pre-filled into the customer's card checkout.</p>
            {isCollection && <p className="mt-2 text-xs font-semibold text-amber-800" data-testid="delivery-override-note">The customer originally chose Collection — freight will now be charged on this quote.</p>}
            {isCollection && <button onClick={() => { setDeliveryOverride(false); setQuote((q) => ({ ...q, freight: 0, freight_source: null })); }} className="mt-2 font-display text-xs font-bold text-ei-navy underline" data-testid="back-to-collection">Back to collection (no freight)</button>}
            <div className="mt-4 space-y-3">
              <div>
                <label className="block text-xs font-semibold text-ei-navy mb-1">Street address</label>
                <input value={address.street} onChange={(e) => setAddress((a) => ({ ...a, street: e.target.value }))} className={selectCls} placeholder="e.g. Unit 3, 24 Fitzgerald Road" data-testid="input-address-street" />
              </div>
              <div>
                <label className="block text-xs font-semibold text-ei-navy mb-1">Suburb / city</label>
                <input value={address.suburb} onChange={(e) => setAddress((a) => ({ ...a, suburb: e.target.value }))} className={selectCls} data-testid="input-address-suburb" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-semibold text-ei-navy mb-1">State</label>
                  <select value={address.state} onChange={(e) => setAddress((a) => ({ ...a, state: e.target.value }))} className={selectCls} data-testid="select-address-state">
                    <option value="">Select</option>
                    {AU_STATES.map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-semibold text-ei-navy mb-1">Postcode</label>
                  <input value={address.postcode} inputMode="numeric" maxLength={4} onChange={(e) => { const postcode = e.target.value.replace(/[^0-9]/g, ""); setAddress((a) => ({ ...a, postcode, state: a.state || stateForPostcode(postcode) })); }} className={`${selectCls} tnum`} data-testid="input-address-postcode" />
                </div>
              </div>
              <label className="flex items-center gap-2 text-xs text-ei-graphite">
                <input type="checkbox" className="h-4 w-4" checked={Boolean(address.residential)} onChange={(e) => setAddress((a) => ({ ...a, residential: e.target.checked }))} data-testid="input-address-residential" />
                Residential / home delivery
              </label>
              <button onClick={saveAddress} disabled={addressSaving || !address.street || !address.suburb || !address.state || address.postcode.length !== 4} className="w-full font-display text-sm font-bold text-ei-navy border border-ei-navy/25 px-5 py-3 rounded-sm hover:border-ei-navy min-h-[44px] inline-flex items-center justify-center gap-2 disabled:opacity-50" data-testid="save-address">
                {addressSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save delivery address
              </button>
            </div>
          </div>
          )}
        </div>

        {/* Right: notes + activity */}
        <div className="lg:col-span-1 space-y-6">
          <div className="bg-white border border-ei-mist rounded-md p-5">
            <h2 className="font-display font-semibold text-ei-navy flex items-center gap-2"><MessageSquarePlus className="w-4 h-4 text-ei-golddark" /> Internal notes</h2>
            <div className="mt-3 flex gap-2">
              <input value={note} onChange={(e) => setNote(e.target.value)} onKeyDown={(e) => e.key === "Enter" && addNote()} placeholder="Add a note…" className="flex-1 min-h-[44px] rounded-sm border border-ei-mist bg-white px-3 py-2 text-sm focus:border-ei-navy outline-none" data-testid="input-note" />
              <button onClick={addNote} className="px-3 min-h-[44px] rounded-sm bg-ei-navy text-ei-white font-semibold text-sm hover:bg-ei-ink inline-flex items-center gap-1" data-testid="add-note"><Send className="w-4 h-4" /></button>
            </div>
            <ul className="mt-4 space-y-3 max-h-64 overflow-y-auto">
              {(enq.notes_log || []).length === 0 && <li className="text-sm text-ei-graphite/50">No notes yet.</li>}
              {[...(enq.notes_log || [])].reverse().map((n) => (
                <li key={n.id} className="bg-ei-white rounded-sm p-3 border border-ei-mist" data-testid={`note-${n.id}`}>
                  <div className="text-sm text-ei-graphite/90">{n.body}</div>
                  <div className="mt-1 text-xs text-ei-graphite/50">{n.author} · {fmtDate(n.created_at, true)}</div>
                </li>
              ))}
            </ul>
          </div>

          <div className="bg-white border border-ei-mist rounded-md p-5">
            <h2 className="font-display font-semibold text-ei-navy flex items-center gap-2"><History className="w-4 h-4 text-ei-golddark" /> Activity history</h2>
            <ul className="mt-4 space-y-3 max-h-72 overflow-y-auto" data-testid="activity-list">
              {activity.map((a) => (
                <li key={a.id} className="flex gap-3">
                  <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-ei-gold flex-shrink-0" />
                  <div>
                    <div className="text-sm text-ei-graphite/90">{a.message}</div>
                    <div className="text-xs text-ei-graphite/50">{a.actor} · {fmtDate(a.timestamp, true)}</div>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>

      <SalesReadiness />
      {/* Quote builder */}
      <div className="mt-6 bg-white border border-ei-mist rounded-md p-5 sm:p-6" data-testid="quote-builder">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <h2 className="font-display font-semibold text-ei-navy flex items-center gap-2"><Receipt className="w-4 h-4 text-ei-golddark" /> Wholesale quote</h2>
          {enq.quote && (() => {
            const st = enq.quote.status;
            const styles = {
              draft: "bg-ei-gold/15 text-ei-golddark border-ei-gold/40",
              sent: "bg-[#061A33] text-ei-white border-[#061A33]",
              accepted: "bg-green-700 text-white border-green-700",
              declined: "bg-red-700 text-white border-red-700",
              expired: "bg-ei-graphite/70 text-white border-ei-graphite/70",
              superseded: "bg-ei-graphite/40 text-white border-ei-graphite/40",
            };
            const label = {
              draft: "draft", sent: `sent ${fmtDate(enq.quote.sent_at, true)}`,
              accepted: `accepted ${fmtDate(enq.quote.accepted_at, true)}`,
              declined: `declined ${fmtDate(enq.quote.declined_at, true)}`,
              expired: "expired", superseded: "superseded",
            }[st] || st;
            return (
              <span className={`text-xs font-semibold px-2.5 py-1 rounded-sm border ${styles[st] || styles.draft}`} data-testid="quote-status">
                v{enq.quote.version} · {label}
                {enq.quote.valid_until && (st === "sent") && <span className="font-normal opacity-80"> · valid until {fmtDate(enq.quote.valid_until, true)}</span>}
              </span>
            );
          })()}
        </div>

        <div className="mt-4 space-y-3">
          {enq.quote?.status === 'accepted' && <p className="rounded-sm border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900" data-testid="accepted-quote-edit-warning">This version was accepted by the customer. Saving creates a replacement draft and blocks payment of the previous offer. The acceptance history remains visible; staff release and customer acceptance are required again.</p>}
          {quote.line_items.map((l, i) => (
            <div key={l.uiId} className="grid grid-cols-12 gap-2 items-end bg-ei-white border border-ei-mist rounded-sm p-3" data-testid={`quote-line-${i}`}>
              <div className="col-span-12 sm:col-span-5">
                <label className="block text-xs font-semibold text-ei-navy mb-1">Product / item</label>
                <input list="quote-products" value={l.name} onChange={(e) => setQLine(i, "name", e.target.value)} className="w-full min-h-[40px] rounded-sm border border-ei-mist px-2.5 py-2 text-sm focus:border-ei-navy outline-none" data-testid={`quote-name-${i}`} />
              </div>
              <div className="col-span-4 sm:col-span-2">
                <label className="block text-xs font-semibold text-ei-navy mb-1">Qty</label>
                <input type="number" min={1} value={l.quantity} onChange={(e) => setQLine(i, "quantity", e.target.value)} className="w-full min-h-[40px] rounded-sm border border-ei-mist px-2.5 py-2 text-sm tnum focus:border-ei-navy outline-none" data-testid={`quote-qty-${i}`} />
              </div>
              <div className="col-span-5 sm:col-span-3">
                <label className="block text-xs font-semibold text-ei-navy mb-1">Unit price (ex GST)</label>
                <input type="number" min={0} step="0.01" value={l.unit_price} onChange={(e) => setQLine(i, "unit_price", e.target.value)} className="w-full min-h-[40px] rounded-sm border border-ei-mist px-2.5 py-2 text-sm tnum focus:border-ei-navy outline-none" data-testid={`quote-price-${i}`} />
              </div>
              <div className="col-span-2 sm:col-span-2 flex items-center justify-between">
                <span className="text-sm font-semibold text-ei-navy tnum">{money((Number(l.quantity) || 0) * (Number(l.unit_price) || 0) * (1 - (Number(l.discount_percent) || 0) / 100))}</span>
                <button type="button" onClick={() => removeQLine(i)} className="w-9 h-9 flex items-center justify-center text-ei-graphite/50 hover:text-red-700" aria-label="Remove line" data-testid={`quote-remove-${i}`}><Trash2 className="w-4 h-4" /></button>
              </div>
              <p className={`col-span-12 text-xs ${l.price_source === 'manual' ? 'text-ei-golddark' : 'text-ei-graphite/60'}`} data-testid={`quote-price-source-${i}`}>
                {l.price_source === 'manual' ? 'Price changed by staff — the catalogue wholesale price was replaced.' : l.price_source === 'catalogue' ? 'Auto-filled from the catalogue wholesale price. Edit to override.' : 'No catalogue match — enter the price manually.'}
              </p>
              <div className="col-span-12 flex flex-wrap gap-3">
                <label className="text-xs text-ei-graphite/70">Discount %<input type="number" min="0" max="100" step="0.01" value={l.discount_percent || 0} onChange={(e) => setQLine(i, 'discount_percent', e.target.value)} className="ml-2 w-20 rounded-sm border border-ei-mist px-2 py-1" data-testid={`quote-discount-${i}`} /></label>
                <input placeholder="Discount reason (required when discount applied)" value={l.discount_reason || ''} onChange={(e) => setQLine(i, 'discount_reason', e.target.value)} className="flex-1 min-w-[200px] rounded-sm border border-ei-mist px-2 py-1 text-xs" data-testid={`quote-discount-reason-${i}`} />
              </div>
            </div>
          ))}
          <datalist id="quote-products">{(catalogue.length ? catalogue.map((p) => p.name) : (meta?.products || [])).map((p) => <option key={p} value={p} />)}</datalist>
          <button type="button" onClick={addQLine} className="inline-flex items-center gap-2 text-sm font-semibold text-ei-navy hover:text-ei-golddark" data-testid="quote-add-line"><Plus className="w-4 h-4" /> Add line item</button>
        </div>

        {freightApplies ? (
          <FreightEstimator key={id} postcode={enq.postcode || ''} address={enq.delivery_address} items={quote.line_items.filter((l) => l.product_id).map((l) => ({ id: l.product_id, quantity: Number(l.quantity) || 1 }))} onAutoFreight={(f) => {
            setQuote((q) => ({ ...q, freight: f.amount_ex_gst === null || f.amount_ex_gst === undefined ? "" : f.amount_ex_gst, freight_source: f.source, freight_quote_required: f.amount_ex_gst === null || f.amount_ex_gst === undefined }));
            if (f.amount_ex_gst === null || f.amount_ex_gst === undefined) toast.error(f.source || "Freight quote required");
            else toast.success(`Freight ${money(Number(f.amount_ex_gst))} ex GST applied from ${f.service_name}`);
          }} />
        ) : (
          <p className="mt-5 rounded-sm border border-ei-mist bg-ei-white/60 px-4 py-3 text-sm text-ei-graphite/80" data-testid="freight-not-required">
            Collection selected by the customer — no freight estimate needed.
          </p>
        )}

        <div className="mt-5 grid sm:grid-cols-3 gap-4">
          <div>
            <label className="block text-xs font-semibold text-ei-navy mb-1">Freight (ex GST)</label>
            <input type="number" min={0} step="0.01" disabled={!freightApplies} value={freightApplies ? quote.freight : 0} onChange={(e) => setQuote((q) => ({ ...q, freight: e.target.value, freight_source: null, freight_quote_required: false }))} className="w-full min-h-[40px] rounded-sm border border-ei-mist px-2.5 py-2 text-sm tnum focus:border-ei-navy outline-none disabled:bg-ei-white/70 disabled:text-ei-graphite/60" data-testid="quote-freight" />
            {!freightApplies ? <p className="mt-1 text-xs font-semibold text-emerald-800" data-testid="quote-freight-collection">Collection — no freight charged</p>
              : quote.freight_quote_required ? <p className="mt-1 text-xs font-semibold text-amber-800" data-testid="quote-freight-required">Freight quote required — Gem could not price this lane. Enter the confirmed freight before sending, or send with $0 only if you are invoicing freight separately.</p>
              : quote.freight_source ? <p className="mt-1 text-xs leading-relaxed text-emerald-800" data-testid="quote-freight-source">Auto: {quote.freight_source}</p>
              : <p className="mt-1 text-xs text-ei-graphite/70" data-testid="quote-freight-manual">Entered manually. Run the estimator above to apply the cheapest confirmed carrier automatically.</p>}          </div>
          <div>
            <label className="block text-xs font-semibold text-ei-navy mb-1">GST rate (%)</label>
            <input type="number" min={0} max={100} step="0.5" value={quote.gst_rate} onChange={(e) => setQuote((q) => ({ ...q, gst_rate: e.target.value }))} className="w-full min-h-[40px] rounded-sm border border-ei-mist px-2.5 py-2 text-sm tnum focus:border-ei-navy outline-none" data-testid="quote-gst" />
          </div>
          <div>
            <label className="block text-xs font-semibold text-ei-navy mb-1">Validity (days)</label>
            <input type="number" min={1} max={365} value={quote.validity_days} onChange={(e) => setQuote((q) => ({ ...q, validity_days: e.target.value }))} className="w-full min-h-[40px] rounded-sm border border-ei-mist px-2.5 py-2 text-sm tnum focus:border-ei-navy outline-none" data-testid="quote-validity" />
          </div>
        </div>

        <div className="mt-4">
          <label className="block text-xs font-semibold text-ei-navy mb-1">Quote notes</label>
          <textarea rows={2} value={quote.notes} onChange={(e) => setQuote((q) => ({ ...q, notes: e.target.value }))} className="w-full rounded-sm border border-ei-mist px-2.5 py-2 text-sm focus:border-ei-navy outline-none" data-testid="quote-notes" />
        </div>

        {/* Totals */}
        <div className="mt-5 flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
          <div className="text-sm text-ei-graphite/85 space-y-1" data-testid="quote-totals">
            {(() => { const t = qTotals(); return (
              <>
                <div className="flex justify-between gap-8"><span>Subtotal (ex GST)</span><span className="tnum font-medium text-ei-navy">{money(t.sub)}</span></div>
                <div className="flex justify-between gap-8"><span>Freight</span><span className="tnum font-medium text-ei-navy">{money(t.freight)}</span></div>
                <div className="flex justify-between gap-8"><span>GST ({Number(quote.gst_rate) || 0}%)</span><span className="tnum font-medium text-ei-navy">{money(t.gst)}</span></div>
                <div className="flex justify-between gap-8 pt-1 border-t border-ei-mist"><span className="font-semibold text-ei-navy">Total (inc GST)</span><span className="tnum font-bold text-ei-navy" data-testid="quote-total">{money(t.total)}</span></div>
              </>
            ); })()}
          </div>
          <div className="flex flex-wrap gap-3">
            <button onClick={saveQuote} disabled={quoteSaving} className="font-display text-sm font-bold text-ei-navy border border-ei-navy/25 px-5 py-3 rounded-sm hover:border-ei-navy min-h-[44px] inline-flex items-center gap-2 disabled:opacity-60" data-testid="quote-save">
              {quoteSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save draft
            </button>
            {(!enq.quote || enq.quote.status === "draft") ? (
              <button onClick={sendQuote} disabled={quoteSending || !enq.quote} title={!enq.quote ? "Save the quote first" : ""} className="font-display text-sm font-bold bg-ei-navy text-ei-white px-5 py-3 rounded-sm hover:bg-ei-ink min-h-[44px] inline-flex items-center gap-2 disabled:opacity-60" data-testid="quote-send">
                {quoteSending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} Release to customer
              </button>
            ) : enq.quote.status === "sent" ? (
              <>
                <span className="text-xs text-ei-graphite/70" data-testid="quote-awaiting-customer">Awaiting acceptance in the customer's trade account</span>
                <button onClick={declineQuote} disabled={quoteDeciding} className="font-display text-sm font-bold text-red-700 border border-red-700/40 px-5 py-3 rounded-sm hover:border-red-700 min-h-[44px] inline-flex items-center gap-2 disabled:opacity-60" data-testid="quote-decline">
                  <XCircle className="w-4 h-4" /> Mark declined
                </button>
              </>
            ) : null}
          </div>
        </div>
        {enq.quote && enq.quote.status !== "draft" && (
          <p className="mt-3 text-xs text-ei-golddark" data-testid="quote-immutable-hint">
            Quote v{enq.quote.version} is {enq.quote.status} and locked. Editing any field and saving will create a new draft version (v{enq.quote.version + 1}).
          </p>
        )}
        {enq.quote?.status === "declined" && enq.quote.decline_reason && (
          <p className="mt-1 text-xs text-ei-graphite/70">Decline reason: {enq.quote.decline_reason}</p>
        )}
        {enq.quote_history && enq.quote_history.length > 0 && (
          <p className="mt-3 text-xs text-ei-graphite/55">{enq.quote_history.length} finalised version(s) on record. Finalised versions are immutable.</p>
        )}
        {enq.quote && (
          <div className="mt-4 pt-4 border-t border-ei-mist flex flex-wrap items-center gap-3">
            <span className="text-xs font-semibold text-ei-graphite/60 uppercase tracking-wide">Saved quote v{enq.quote.version}</span>
            <button onClick={downloadQuotePdf} disabled={pdfBusy} className="inline-flex items-center gap-2 font-display text-sm font-bold text-ei-navy border border-ei-navy/25 px-4 py-2.5 rounded-sm hover:border-ei-navy transition-colors min-h-[44px] disabled:opacity-60" data-testid="quote-download-pdf">
              {pdfBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileText className="w-4 h-4" />} Download PDF
            </button>
            <button onClick={emailQuote} disabled={emailBusy} className="inline-flex items-center gap-2 font-display text-sm font-bold text-ei-navy border border-ei-navy/25 px-4 py-2.5 rounded-sm hover:border-ei-navy transition-colors min-h-[44px] disabled:opacity-60" data-testid="quote-email-pdf">
              {emailBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Mail className="w-4 h-4" />} Email to contractor
            </button>
            <span className="text-xs text-ei-graphite/50">Emails the branded PDF to {enq.email}</span>
          </div>
        )}
        {meta?.xero_enabled && enq.quote?.status === "accepted" && (
          <div className="mt-4 pt-4 border-t border-ei-mist" data-testid="xero-panel">
            <div className="flex flex-wrap items-center gap-3">
              <span className="text-xs font-semibold text-ei-graphite/60 uppercase tracking-wide flex items-center gap-1.5"><Receipt className="w-3.5 h-3.5 text-ei-golddark" /> Xero accounting</span>
              {!meta?.xero_connected || meta?.xero_needs_reauth ? (
                <button onClick={connectXero} className="inline-flex items-center gap-2 font-display text-sm font-bold text-ei-navy border border-ei-navy/25 px-4 py-2.5 rounded-sm hover:border-ei-navy transition-colors min-h-[44px]" data-testid="xero-connect">
                  <ExternalLink className="w-4 h-4" /> {meta?.xero_needs_reauth ? "Reconnect Xero (new permissions)" : "Connect Xero"}
                </button>
              ) : !enq.quote.xero?.invoice_id ? (
                <button onClick={createXeroInvoice} disabled={xeroBusy} className="inline-flex items-center gap-2 font-display text-sm font-bold bg-[#13B5EA] text-white px-4 py-2.5 rounded-sm hover:brightness-95 transition-all min-h-[44px] disabled:opacity-60" data-testid="xero-create-invoice">
                  {xeroBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Receipt className="w-4 h-4" />} Create draft invoice
                </button>
              ) : (
                <>
                  <span className="text-xs font-semibold px-2.5 py-1 rounded-sm border bg-[#13B5EA] text-white border-[#13B5EA]" data-testid="xero-invoice-status">
                    {enq.quote.xero.status || "DRAFT"} · {enq.quote.xero.invoice_number}
                  </span>
                  {enq.quote.xero.total && <span className="text-xs text-ei-graphite/60">Xero total: {enq.quote.xero.currency || "AUD"} {enq.quote.xero.total}</span>}
                </>
              )}
            </div>
            <p className="mt-2 text-xs text-ei-graphite/55">Creates a DRAFT accounts-receivable invoice in Xero from the accepted quote (AUD, GST added by Xero). Review and send it from inside Xero. Re-running won't create a duplicate.</p>
          </div>
        )}
      </div>
    </div>
  );
}

function toLocalInput(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    const off = d.getTimezoneOffset();
    const local = new Date(d.getTime() - off * 60000);
    return local.toISOString().slice(0, 16);
  } catch {
    return "";
  }
}
